import { z } from 'zod';
import {
  BaseResponseSchema,
  baconStatusSchema,
  dateSchema,
  emailSchema,
  integerIdSchema,
  phoneNumberSchema,
} from './primitives.js';

import { UserSummarySchema } from './tenant-directory.schemas.js';

// Location Tracking Schemas


// A beacon/tag window that overlaps a wafer's container stay, for frontend UWB stitching
export const TagWindowForContainerSchema = z.object({
  beaconId: z.string(),
  beaconVersion: z.number().nullable(),
  from: dateSchema,
  to: dateSchema.nullable()
});
// One period during which a wafer resided in a specific container, enriched with tag windows
export const WaferContainerStaySchema = z.object({
  containerId: integerIdSchema,
  containerName: z.string(),
  slotNumber: z.number().nullable(),
  fromDate: dateSchema,
  toDate: dateSchema.nullable(),
  transferType: z.string(),
  tagWindows: z.array(TagWindowForContainerSchema)
});
export const DateWindowSchema = z.object({
  startDate: dateSchema.optional(),
  endDate: dateSchema.optional()
});

/** Provider tenant utilization CSV export query (`YYYY-MM-DD` inclusive range). */
export const TenantUtilizationExportQuerySchema = z
  .object({
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be YYYY-MM-DD'),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'endDate must be YYYY-MM-DD'),
  })
  .refine((q) => q.startDate <= q.endDate, {
    message: 'startDate must be on or before endDate',
  });







export const BeaconAssignmentSchema = z.object({
  beaconId: z.string().min(1),
  beaconVersion: z.number().nullable(),
  assignedDate: dateSchema,
  removedDate: dateSchema.nullable(),
  durationDays: z.number().nullable(),
  assignedBy: UserSummarySchema.nullable(),
  isCurrent: z.boolean()
});
export const ContainerBeaconHistoryResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    containerId: integerIdSchema,
    containerName: z.string(),
    tagAssignments: z.array(BeaconAssignmentSchema)
  })
});


// Beacon Configuration Schemas
export const CreateBeaconConfigRequestSchema = z.object({
  beaconId: z.string(),
  version: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  locationName: z.string().optional(),
  defaultLocationType: z.string(),
  defaultLatitude: z.number().optional(),
  defaultLongitude: z.number().optional(),
  defaultFloor: z.string().optional(),
  defaultRoom: z.string().optional(),
  defaultArea: z.string().optional(),
  updateIntervalSeconds: z.number().int().min(1).optional(),
  lowBatteryThreshold: z.number().optional(),
  configuration: z.record(z.any()).optional()
});
export const UpdateBeaconConfigRequestSchema = z.object({
  version: z.string().optional(),
  status: baconStatusSchema.optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  locationName: z.string().optional(),
  defaultLocationType: z.string().optional(),
  defaultLatitude: z.number().optional(),
  defaultLongitude: z.number().optional(),
  defaultFloor: z.string().optional(),
  defaultRoom: z.string().optional(),
  defaultArea: z.string().optional(),
  updateIntervalSeconds: z.number().int().min(1).optional(),
  lowBatteryThreshold: z.number().optional(),
  lastMaintenance: dateSchema.optional(),
  nextMaintenance: dateSchema.optional(),
  configuration: z.record(z.any()).optional(),
  isActive: z.boolean().optional()
});
export const GetBeaconConfigsRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  status: baconStatusSchema.optional(),
  locationType: z.string().optional(),
  sortBy: z.enum(['beaconId', 'lastSeen', 'status', 'createdAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});
// Location Zone Schemas
export const CreateLocationZoneRequestSchema = z.object({
  zoneCode: z.string().min(1).max(50).trim(),
  zoneName: z.string().min(1).max(100).trim(),
  locationType: z.string(),
  description: z.string().optional(),
  building: z.string().optional(),
  floor: z.string().optional(),
  room: z.string().optional(),
  area: z.string().optional(),
  centerLatitude: z.number().optional(),
  centerLongitude: z.number().optional(),
  radiusMeters: z.number().optional(),
  boundaryCoordinates: z.array(z.object({
    lat: z.number(),
    lng: z.number()
  })).optional(),
  targetTemperature: z.number().optional(),
  targetHumidity: z.number().optional(),
  temperatureTolerance: z.number().optional(),
  humidityTolerance: z.number().optional(),
  requiresAuthorization: z.boolean().optional(),
  authorizedGroups: z.array(z.string()).optional(),
  contactPerson: z.string().optional(),
  contactEmail: emailSchema.optional(),
  contactPhone: phoneNumberSchema.optional(),
  operatingHours: z.object({
    monday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    tuesday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    wednesday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    thursday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    friday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    saturday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    sunday: z.object({
      start: z.string(),
      end: z.string()
    }).optional()
  }).optional()
});

export const UpdateLocationZoneRequestSchema = z.object({
  zoneName: z.string().min(1).max(100).trim().optional(),
  locationType: z.string().optional(),
  description: z.string().optional(),
  building: z.string().optional(),
  floor: z.string().optional(),
  room: z.string().optional(),
  area: z.string().optional(),
  centerLatitude: z.number().optional(),
  centerLongitude: z.number().optional(),
  radiusMeters: z.number().optional(),
  boundaryCoordinates: z.array(z.object({
    lat: z.number(),
    lng: z.number()
  })).optional(),
  targetTemperature: z.number().optional(),
  targetHumidity: z.number().optional(),
  temperatureTolerance: z.number().optional(),
  humidityTolerance: z.number().optional(),
  requiresAuthorization: z.boolean().optional(),
  authorizedGroups: z.array(z.string()).optional(),
  contactPerson: z.string().optional(),
  contactEmail: emailSchema.optional(),
  contactPhone: phoneNumberSchema.optional(),
  operatingHours: z.object({
    monday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    tuesday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    wednesday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    thursday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    friday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    saturday: z.object({
      start: z.string(),
      end: z.string()
    }).optional(),
    sunday: z.object({
      start: z.string(),
      end: z.string()
    }).optional()
  }).optional(),
  isActive: z.boolean().optional()
});

export const GetLocationZonesRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  locationType: z.string().optional(),
  building: z.string().optional(),
  floor: z.string().optional(),
  requiresAuthorization: z.coerce.boolean().optional(),
  isActive: z.coerce.boolean().optional(),
  sortBy: z.enum(['zoneCode', 'zoneName', 'locationType', 'createdAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});

export const LocationTypesResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    locationTypes: z.array(z.object({
      type: z.string(),
      description: z.string(),
      providers: z.array(z.string()),
    })),
  }),
});

export const SyncLocationResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    locationsProcessed: z.number().int(),
    beaconsProcessed: z.number().int(),
    errors: z.array(z.string()).optional(),
  }),
});

export const LocationWebhookResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    id: integerIdSchema,
    timestamp: dateSchema,
    locationType: z.string(),
    provider: z.string(),
  }),
});

export const LocationStatsResponseSchema = BaseResponseSchema.extend({
  data: z.record(z.object({
    totalLocations: z.number().int(),
    activeBeacons: z.number().int(),
    lastSync: dateSchema.optional(),
    syncStatus: z.string(),
  })),
});

export const ValidateLocationTypeResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    isValid: z.boolean(),
    supportedProviders: z.array(z.string()),
    errors: z.array(z.string()).optional(),
  }),
});

export const ConfigureProviderResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    provider: z.string(),
    enabled: z.boolean(),
    configured: z.boolean(),
    lastConfigured: dateSchema.optional(),
  }),
});

export const ProvidersListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    providers: z.array(z.object({
      name: z.string(),
      version: z.string(),
      enabled: z.boolean(),
      status: z.string().optional(),
    })),
  }),
});
// Inferred types
export type BeaconAssignment = z.infer<typeof BeaconAssignmentSchema>;
export type ConfigureProviderResponse = z.infer<typeof ConfigureProviderResponseSchema>;
export type ContainerBeaconHistoryResponse = z.infer<typeof ContainerBeaconHistoryResponseSchema>;
export type ContainerBeaconHistoryResponseData = z.infer<typeof ContainerBeaconHistoryResponseSchema.shape.data>;
export type CreateBeaconConfigRequest = z.infer<typeof CreateBeaconConfigRequestSchema>;
export type CreateLocationZoneRequest = z.infer<typeof CreateLocationZoneRequestSchema>;
export type DateWindow = z.infer<typeof DateWindowSchema>;
export type GetBeaconConfigsRequest = z.infer<typeof GetBeaconConfigsRequestSchema>;
export type GetLocationZonesRequest = z.infer<typeof GetLocationZonesRequestSchema>;
export type LocationStatsResponse = z.infer<typeof LocationStatsResponseSchema>;
export type LocationTypesResponse = z.infer<typeof LocationTypesResponseSchema>;
export type LocationWebhookResponse = z.infer<typeof LocationWebhookResponseSchema>;
export type ProvidersListResponse = z.infer<typeof ProvidersListResponseSchema>;
export type SyncLocationResponse = z.infer<typeof SyncLocationResponseSchema>;
export type TagWindowForContainer = z.infer<typeof TagWindowForContainerSchema>;
export type TenantUtilizationExportQuery = z.infer<typeof TenantUtilizationExportQuerySchema>;
export type UpdateBeaconConfigRequest = z.infer<typeof UpdateBeaconConfigRequestSchema>;
export type UpdateLocationZoneRequest = z.infer<typeof UpdateLocationZoneRequestSchema>;
export type ValidateLocationTypeResponse = z.infer<typeof ValidateLocationTypeResponseSchema>;
export type WaferContainerStay = z.infer<typeof WaferContainerStaySchema>;
